import React, { useCallback, useMemo, useState } from 'react';
import {
  Platform,
} from 'react-native';
import { showAlert } from '@/utils/common/alert';
import { StackScreenProps } from '@react-navigation/stack';
import { AdminStackParamList } from '@/navigation/admin/AdminNavigator';
import { useResponsive } from '@/hooks/common/useResponsive';
import { useCreateEventMutation, convertToFormData } from '@/store/api/admin/eventsApi';
import { useAdminUser } from '@/hooks/admin/useAdminUser';
import { useToast, useErrorToast } from '@/contexts/admin/ToastContext';
import { isNetworkError } from '@/utils/admin/errorHandling';
import { useCreateEventForm } from '@/components/admin/events/CreateEvent/hooks/useCreateEventForm';
import { useDatePicker } from '@/components/admin/events/CreateEvent/hooks/useDatePicker';
import { EventFormSections } from '@/components/admin/events/CreateEvent/components/EventFormSections';
import { EventDateTimePickers } from '@/components/admin/events/CreateEvent/components/EventDateTimePickers';
import { AnchorRect } from '@/hooks/common/useAnchoredPosition';
import { DesktopFormPage } from '@/components/common/admin/desktop/DesktopFormPage';
import { MobileFormScreen } from '@/components/common/admin/mobile/MobileFormScreen';
import { PageHeader } from '@/components/shared/redesign';

type CreateEventScreenProps = StackScreenProps<AdminStackParamList, 'CreateEvent'>;

export const CreateEventScreen: React.FC<CreateEventScreenProps> = ({
  navigation,
}) => {
  const { layoutMode } = useResponsive();
  const { showSuccess, showLoading, hideLoading, showWarning } = useToast();
  const { showApiError } = useErrorToast();
  const adminUser = useAdminUser();
  const selectedPropertyId = adminUser.selectedPropertyId;  
  const [createEvent, { isLoading }] = useCreateEventMutation();

  // Custom hooks for form and date picker logic
  const {
    formData,
    errors,
    validateForm,
    handleFieldChange,
    handlePublishTypeChange,
    appendPhotos,
    showDiscardAlert,
  } = useCreateEventForm();

  const {
    datePickerState,
    DateTimePicker,
    handleDatePickerChange,
    showStartDatePicker,
    showEndDatePicker,
    showPublishDatePicker,
    hideDatePickers,
    confirmDateSelection,
    showFallbackDatePicker,
    setDatePickerMode,
  } = useDatePicker();

  // Memoized date selection handler to prevent unnecessary re-renders
  const handleDateSelection = useCallback((date: Date, type: 'start' | 'end' | 'publish') => {
    if (type === 'start') {
      handleFieldChange('startTime', date);
      // Auto-adjust end time if it's before the new start time
      if (date >= formData.endTime) {
        const newEndTime = new Date(date);
        newEndTime.setHours(date.getHours() + 1, 0, 0, 0);
        handleFieldChange('endTime', newEndTime);
      }
    } else if (type === 'end') {
      handleFieldChange('endTime', date);
    } else if (type === 'publish') {
      handleFieldChange('publishTime', date);
    }
  }, [formData.endTime, handleFieldChange]);

  // Date picker handlers with fallback support
  const handleStartTimePress = useCallback((anchor?: AnchorRect) => {
    if (Platform.OS === 'web') {
      // store anchor in state to pass into modal dropdown
      setWebAnchor(anchor || null);
      showStartDatePicker(formData.startTime);
      return;
    }
    if (DateTimePicker) {
      showStartDatePicker(formData.startTime);
    } else {
      showFallbackDatePicker('Start Time', formData.startTime, (date) => {
        handleDateSelection(date, 'start');
      });
    }
  }, [DateTimePicker, formData.startTime, showStartDatePicker, showFallbackDatePicker, handleDateSelection]);

  const handleEndTimePress = useCallback((anchor?: AnchorRect) => {
    if (Platform.OS === 'web') {
      setWebAnchor(anchor || null);
      showEndDatePicker(formData.endTime);
      return;
    }
    if (DateTimePicker) {
      showEndDatePicker(formData.endTime);
    } else {
      showFallbackDatePicker('End Time', formData.endTime, (date) => {
        handleDateSelection(date, 'end');
      });
    }
  }, [DateTimePicker, formData.endTime, showEndDatePicker, showFallbackDatePicker, handleDateSelection]);

  const handlePublishTimePress = useCallback((anchor?: AnchorRect) => {
    if (Platform.OS === 'web') {
      setWebAnchor(anchor || null);
      showPublishDatePicker(formData.publishTime || new Date());
      return;
    }
    if (DateTimePicker) {
      showPublishDatePicker(formData.publishTime || new Date());
    } else {
      showFallbackDatePicker('Publish Time', formData.publishTime || new Date(), (date) => {
        handleDateSelection(date, 'publish');
      });
    }
  }, [DateTimePicker, formData.publishTime, showPublishDatePicker, showFallbackDatePicker, handleDateSelection]);

  // Web-only anchor state for dropdown positioning
  const [webAnchor, setWebAnchor] = useState<AnchorRect | null>(null);

  // Form submission handler
  const handleSubmit = useCallback(async () => {
    if (!validateForm()) {
      return;
    }

    if (!selectedPropertyId) {
      showAlert('Error', 'No property selected');
      return;
    }

    const loadingId = showLoading('Creating event...');
    try {
      // Convert form data to the format expected by the API
      const eventData = {
        name: formData.name,
        description: formData.description,
        location: formData.location,
        startTime: formData.startTime.toISOString(),
        endTime: formData.endTime.toISOString(),
        rsvpLimit: parseInt(formData.rsvpLimit, 10),
        internalNotes: formData.internalNotes,
        publishType: formData.publishType,
        publishTime: formData.publishTime?.toISOString(),
        photos: formData.photos,
      };

      const formDataToSend = convertToFormData(eventData, selectedPropertyId);
      const result = await createEvent(formDataToSend).unwrap();
      
      hideLoading(loadingId);
      
      // Check for partial success (event created but photos failed to upload)
      if (result.warning) {
        showWarning(`Event created successfully, but ${result.warning}`, {
          duration: 6000, // Longer duration for important warning
        });
      } else {
        showSuccess('Event created successfully!');
      }
      navigation.goBack();
    } catch (error: any) {
      hideLoading(loadingId);
      console.error('Failed to create event:', error);
      
      // Improved error message for network issues
      if (isNetworkError(error)) {
        showApiError(error, 'Network error. Please check your connection and try again.');
      } else {
        showApiError(error, 'Failed to create event');
      }
    }
  }, [
    validateForm,
    selectedPropertyId,
    formData,
    createEvent,
    showSuccess,
    showWarning,
    showLoading,
    hideLoading,
    showApiError,
    navigation,
  ]);

  // Cancel handler
  const handleCancel = useCallback(() => {
    if (Platform.OS === 'web') {
      // DesktopFormPage handles confirmation on web; navigate back on confirm
      navigation.goBack();
      return;
    }
    showDiscardAlert(() => navigation.goBack());
  }, [showDiscardAlert, navigation]);

  // Date picker confirmation handler
  const handleDatePickerConfirm = useCallback((pickerType: 'start' | 'end' | 'publish', selectedDate?: Date) => {
    if (selectedDate) {
      // Android: date passed directly to avoid stale closure
      handleDateSelection(selectedDate, pickerType);
      hideDatePickers();
    } else {
      // iOS/web: read from tempDate state via confirm button
      confirmDateSelection(pickerType, handleDateSelection);
    }
  }, [confirmDateSelection, handleDateSelection, hideDatePickers]);

  // Memoized scroll content to prevent unnecessary re-renders
  const scrollContent = useMemo(() => (
    <EventFormSections
      formData={formData}
      errors={errors}
      onFieldChange={handleFieldChange}
      appendPhotos={appendPhotos}
      onStartTimePress={handleStartTimePress}
      onEndTimePress={handleEndTimePress}
      onPublishTypeChange={handlePublishTypeChange}
      onPublishTimePress={handlePublishTimePress}
    />
  ), [
    formData,
    errors,
    handleFieldChange,
    appendPhotos,
    handleStartTimePress,
    handleEndTimePress,
    handlePublishTypeChange,
    handlePublishTimePress,
  ]);

  // Desktop web rendering to match admin-web
  if (layoutMode === 'desktop') {
    return (
      <>
        <DesktopFormPage
          title="Create Event"
          subtitle="Add event details"
          isSubmitting={isLoading}
          submitText="Create"
          onSubmit={handleSubmit}
          onCancel={handleCancel}
        >
          {scrollContent}
        </DesktopFormPage>
        <EventDateTimePickers
          DateTimePicker={DateTimePicker}
          datePickerState={datePickerState}
          onDateChange={handleDatePickerChange}
          onConfirm={handleDatePickerConfirm}
          onCancel={hideDatePickers}
          onModeChange={setDatePickerMode}
          webAnchor={webAnchor}
        />
      </>
    );
  }

  return (
    <>
      <MobileFormScreen
        screenName="CreateEvent"
        navigation={navigation}
        isSubmitting={isLoading}
        submitText="Create"
        onSubmit={handleSubmit}
        onCancel={handleCancel}
      >
        <PageHeader title="Create event" subtitle="Add details for a new event" />
        {scrollContent}
      </MobileFormScreen>
      <EventDateTimePickers
        DateTimePicker={DateTimePicker}
        datePickerState={datePickerState}
        onDateChange={handleDatePickerChange}
        onConfirm={handleDatePickerConfirm}
        onCancel={hideDatePickers}
        onModeChange={setDatePickerMode}
        webAnchor={webAnchor}
      />
    </>
  );
};

